/**
 * Pending Answers Panel
 *
 * Component for reviewing and approving/rejecting generated answers
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { authFetch } from '@/utils/auth-fetch';
import { Loader2, CheckCircle, XCircle, Eye, Link as LinkIcon } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface Answer {
  id: string;
  question_id: string;
  question_text: string;
  answer_text: string;
  answer_html: string;
  status: string;
  wheel_name: string;
  category: string;
  entity_count: number;
  created_at: string;
  metadata: any;
}

interface PendingAnswersPanelProps {
  siteId: string;
  onUpdate?: () => void;
}

export function PendingAnswersPanel({ siteId, onUpdate }: PendingAnswersPanelProps) {
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState<Answer | null>(null);
  const [actionDialog, setActionDialog] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const { toast } = useToast();

  useEffect(() => {
    loadAnswers();
  }, [siteId, statusFilter]);

  const loadAnswers = async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        `/qa/sites/${siteId}/answers/pending?status=${statusFilter}&limit=50&offset=0`
      );
      const data = await response.json();

      if (data.success) {
        setAnswers(data.answers);
      }
    } catch (error) {
      console.error('Failed to load answers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load answers',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedAnswer) return;

    setProcessing(true);
    try {
      const response = await authFetch(
        `/qa/sites/${siteId}/answers/${selectedAnswer.id}/approve`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes })
        }
      );

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Answer Approved',
          description: 'Answer has been published to knowledge base'
        });

        // Refresh the list
        loadAnswers();
        onUpdate?.();

        // Close dialog
        setActionDialog(null);
        setSelectedAnswer(null);
        setNotes('');
      } else {
        throw new Error(data.error || 'Approval failed');
      }
    } catch (error: any) {
      toast({
        title: 'Approval Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedAnswer || !rejectionReason) return;

    setProcessing(true);
    try {
      const response = await authFetch(
        `/qa/sites/${siteId}/answers/${selectedAnswer.id}/reject`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: rejectionReason, notes })
        }
      );

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Answer Rejected',
          description: 'Answer has been marked as rejected'
        });

        // Refresh the list
        loadAnswers();
        onUpdate?.();

        // Close dialog
        setActionDialog(null);
        setSelectedAnswer(null);
        setRejectionReason('');
        setNotes('');
      } else {
        throw new Error(data.error || 'Rejection failed');
      }
    } catch (error: any) {
      toast({
        title: 'Rejection Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Answer Review Queue</CardTitle>
              <CardDescription>
                Review AI-generated answers before publication
              </CardDescription>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : answers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No {statusFilter} answers found
            </div>
          ) : (
            <div className="space-y-4">
              {answers.map((answer) => (
                <Card key={answer.id}>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">{answer.wheel_name}</Badge>
                            <Badge variant="secondary">{answer.category}</Badge>
                            {answer.entity_count > 0 && (
                              <Badge variant="default" className="gap-1">
                                <LinkIcon className="h-3 w-3" />
                                {answer.entity_count} entities
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-semibold mb-2">{answer.question_text}</h4>
                          <div
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: answer.answer_html || answer.answer_text }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="text-xs text-muted-foreground">
                          Generated {new Date(answer.created_at).toLocaleDateString()}
                        </div>
                        {statusFilter === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedAnswer(answer);
                                setActionDialog('reject');
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedAnswer(answer);
                                setActionDialog('approve');
                              }}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={actionDialog === 'approve'} onOpenChange={(open) => !open && setActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Answer</DialogTitle>
            <DialogDescription>
              This answer will be published to your knowledge base and made available publicly.
            </DialogDescription>
          </DialogHeader>

          {selectedAnswer && (
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-sm">{selectedAnswer.question_text}</p>
              </div>

              <div>
                <Label htmlFor="approve-notes">Notes (Optional)</Label>
                <Textarea
                  id="approve-notes"
                  placeholder="Add any notes about this approval..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={processing}>
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Approve & Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={actionDialog === 'reject'} onOpenChange={(open) => !open && setActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Answer</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this answer.
            </DialogDescription>
          </DialogHeader>

          {selectedAnswer && (
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-sm">{selectedAnswer.question_text}</p>
              </div>

              <div>
                <Label htmlFor="rejection-reason">Rejection Reason *</Label>
                <Select value={rejectionReason} onValueChange={setRejectionReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inaccurate">Inaccurate Information</SelectItem>
                    <SelectItem value="incomplete">Incomplete Answer</SelectItem>
                    <SelectItem value="tone">Wrong Tone/Style</SelectItem>
                    <SelectItem value="entities">Missing/Wrong Entities</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="reject-notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="reject-notes"
                  placeholder="Add any additional notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={processing}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processing || !rejectionReason}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reject Answer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
